import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const CDN = 'https://static.prod-images.emergentagent.com/jobs/466e3310-a725-4cde-af13-49d0bcf09ec9/images';

/** The five readings, his wording — and a painting for each, because a colour is not a feeling. */
export const MOOD_IMAGES = {
  1: `${CDN}/06443443701817a545478b0791525d83e075231256237e858063b29788cedf5a.jpeg`,
  2: `${CDN}/f90719b65ec70aa224cf7cf73209f0bbb4119ef86d303417ba71217f1a70f60c.jpeg`,
  3: `${CDN}/30227285e2157c0aade82af3ac6c7b50f40d37ff30d79dc3226c1c04e6b2c3aa.jpeg`,
  4: `${CDN}/34d055d71bc851c81caa3725da29f5cd52cd9699357d3bcf6abfbebd06b737bb.jpeg`,
  5: `${CDN}/16fe9fcfaa43cbe16ac87aaed869aaf914e0378192fccec9cac591fbd918f8b4.jpeg`,
};

export const MOOD_STYLE = {
  1: { ring: 'border-indigo-400/50', fill: 'bg-indigo-400', text: 'text-indigo-200' },
  2: { ring: 'border-rose-300/50', fill: 'bg-rose-300', text: 'text-rose-200' },
  3: { ring: 'border-slate-300/40', fill: 'bg-slate-300', text: 'text-slate-200' },
  4: { ring: 'border-teal-300/50', fill: 'bg-teal-300', text: 'text-teal-200' },
  5: { ring: 'border-amber-300/50', fill: 'bg-amber-300', text: 'text-amber-200' },
};

export const MOOD_FALLBACK = {
  1: 'Deeply heavy · drained',
  2: 'Low · tender',
  3: 'Neutral · steady',
  4: 'Open · grounded',
  5: 'Expansive · aligned',
};

/**
 * The image is the selector; the words sit underneath. Tappable as many times a day as they
 * like — a mood is a passing thing, not a daily verdict.
 *
 * `onPick` makes it a plain chooser (the journal composer uses that, so the reading is saved
 * with the entry). Without it, tapping logs the reading straight away.
 */
export const MoodPicker = ({ moods, latest, loggedToday = 0, onLogged, onPick,
                             compact = false }) => {
  const labels = moods || MOOD_FALLBACK;
  const [busy, setBusy] = useState(false);

  const choose = async (mood) => {
    if (onPick) { onPick(mood); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/companion/mood`, { mood, note: '' });
      toast.success(`Noted — ${res.data.label}`);
      onLogged?.(res.data);
    } catch {
      toast.error('That reading was not saved');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="mood-picker">
      <div className={`grid grid-cols-5 ${compact ? 'gap-1.5' : 'gap-3 sm:gap-4'}`}>
        {[1, 2, 3, 4, 5].map((mood) => {
          const on = latest === mood;
          return (
            <button
              key={mood}
              onClick={() => choose(mood)}
              disabled={busy}
              aria-label={labels[mood]}
              aria-pressed={on}
              className={`group flex flex-col items-center gap-2 rounded-xl p-1 disabled:opacity-50
                          transition-all duration-300 hover:-translate-y-1`}
              data-testid={`mood-option-${mood}`}
            >
              <span
                className={`relative block w-full overflow-hidden rounded-xl border-2
                            ${on ? 'border-white/80 shadow-lg shadow-black/50'
                                 : 'border-white/10 group-hover:border-white/40'}
                            transition-colors duration-300`}
                style={{ aspectRatio: '1 / 1' }}
              >
                <img
                  src={MOOD_IMAGES[mood]}
                  alt=""
                  loading="lazy"
                  className={`w-full h-full object-cover transition-all duration-500
                              ${on ? 'saturate-125' : 'saturate-75 group-hover:saturate-110'}`}
                />
                {on && (
                  <span className="absolute inset-0 ring-2 ring-inset ring-white/40 rounded-xl" />
                )}
              </span>
              <span className={`text-center leading-tight ${compact ? 'text-[9px]' : 'text-[11px] sm:text-xs'}
                                ${on ? 'text-white' : 'text-white/55 group-hover:text-white/85'}
                                transition-colors duration-300`}>
                {labels[mood]}
              </span>
            </button>
          );
        })}
      </div>
      {compact && (
        <p className="text-[11px] text-white/40" data-testid="mood-picker-caption">
          {latest ? labels[latest] : 'Tap where you are'}
          {loggedToday > 1 ? ` · ${loggedToday} readings today` : ''}
        </p>
      )}
    </div>
  );
};

export default MoodPicker;
